import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { ESGImpactIcon } from 'modules/ESG';

import { InfoTooltip } from 'ui';
import { useReportPage } from 'shared/hooks';
import { LegendColorChip } from './LegendColorChip/LegendColorChip';

const useStyles = makeStyles(theme => ({
  container: {
    flexGrow: 1,
  },
  columnDisplay: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  row: {
    '& + &': {
      marginTop: theme.spacing(1),
    },
  },
  columnRow: {
    width: 410,
  },
  label: {
    width: 'auto',
  },
  score: {
    width: 'auto',
    marginRight: ({ column }) => (column ? 0 : theme.spacing(2)),
  },
  scoreColumn: {
    height: 32,
  },
  helpIcon: {
    marginRight: theme.spacing(1),
    cursor: 'pointer',
  },
  legendIcon: {
    marginRight: ({ column }) => (column ? 0 : theme.spacing(1)),
    transform: useReportPage ? 'scale(0.7)' : 'none',
  },
  legendLabel: {
    fontSize: useReportPage ? 10 : 14,
  },
  columnLabel: {
    fontSize: useReportPage ? 10 : 14,
    marginBottom: ({ column }) => column && 10,
  },
}));

export const Legend = ({ column }) => {
  const classes = useStyles({ column });
  const { t } = useTranslation();

  const scores = [
    {
      impactLevel: 'veryHigh',
      label: t('Very high'),
    },
    {
      impactLevel: 'high',
      label: t('High'),
    },
    {
      impactLevel: 'medium',
      label: t('Medium'),
    },
    {
      impactLevel: 'low',
      label: t('Low'),
    },
    {
      impactLevel: 'veryLow',
      label: t('Very low'),
    },
    {
      impactLevel: 'insignificant',
      label: t('Insignificant'),
    },
  ];

  const weights = [
    {
      impactWeight: 'high',
      label: t('High (25% and above)'),
    },
    {
      impactWeight: 'average',
      label: t('Average (10% to 25%)'),
    },
    {
      impactWeight: 'low',
      label: t('Low (3% to 10%)'),
    },
  ];

  return (
    <div className={clsx(classes.container, column && classes.columnDisplay)}>
      <Grid
        container
        className={classes.row}
        direction={column ? 'column' : 'row'}
        justifyContent="flex-start"
        alignItems={column ? 'flex-start' : 'center'}
      >
        <Grid
          container
          className={classes.score}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          {!useReportPage && (
            <InfoTooltip
              iconClassName={classes.helpIcon}
              title={t(
                'The intensity measure reveals how intensive are the company’s externalities in a specific region/industry compared to other companies in that same region/industry.',
              )}
            />
          )}
          <strong className={classes.columnLabel}>{t('Intensity')}:</strong>
        </Grid>
        {[...scores].map(({ impactLevel, label }) => (
          <Grid
            key={impactLevel}
            className={clsx(classes.score, column && classes.scoreColumn)}
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <LegendColorChip impactLevel={impactLevel} className={classes.legendIcon} />
            <Typography variant="body2" className={classes.legendLabel}>
              {label}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Grid
        container
        className={clsx(column ? classes.columnRow : classes.row)}
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
      >
        <Grid
          container
          className={classes.score}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          {!useReportPage && (
            <InfoTooltip
              iconClassName={classes.helpIcon}
              title={t(
                'The weights represent the company’s perspective: they are based on the value of the company’s externalities in a specific region/industry compared to the sum of all externalities for that company. Their sum is 100%.',
              )}
            />
          )}
          <strong className={classes.columnLabel}>{t('Weights')}:</strong>
        </Grid>
        {[...weights].map(({ impactWeight, label }) => (
          <Grid
            key={impactWeight}
            className={classes.score}
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <ESGImpactIcon impactWeight={impactWeight} className={classes.legendIcon} />
            <Typography variant="body2" className={classes.legendLabel}>
              {label}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

Legend.defaultProps = {
  column: false,
};

Legend.propTypes = {
  column: PropTypes.bool,
};
